import React, { useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, ProgressBar, Row } from 'react-bootstrap';
import { ArrowUp, CheckCircle, ExclamationTriangle, Upload, XCircle } from 'react-bootstrap-icons';
import { GoProMetaDataReader, GoProMetadata } from '../../utils/goProMetaDataReader';

interface DurationData {
  duration: number;
  isEstimated: boolean;
}

interface VideoCheckResult {
  file: File;
  status: 'checking' | 'valid' | 'corrupted' | 'error' | 'undefined';
  cameraId?: string;
  videoDate?: Date;
  duration?: DurationData;
  error?: string;
}

interface TimelineVideoData {
  result: VideoCheckResult;
  duration: number;
  startTime: number;
  endTime: number;
}

const VideoCorruptionCheckPage: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [checkResults, setCheckResults] = useState<VideoCheckResult[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [currentCheckIndex, setCurrentCheckIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAreaRef = useRef<HTMLDivElement>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    uploadAreaRef.current?.classList.add('dragover');
  };

  const handleDragLeave = () => {
    uploadAreaRef.current?.classList.remove('dragover');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    uploadAreaRef.current?.classList.remove('dragover');
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    handleFiles(files);
  };

  const handleFiles = (files: File[]) => {
    const videoFiles = files.filter(file =>
      file.type.startsWith('video/') ||
      file.name.toLowerCase().endsWith('.360') ||
      file.name.toLowerCase().endsWith('.mp4') ||
      file.name.toLowerCase().endsWith('.mov') ||
      file.name.toLowerCase().endsWith('.avi')
    );

    setSelectedFiles(videoFiles);
    setCheckResults([]);
    setCurrentCheckIndex(0);
  };

  // Extract video duration
  const extractVideoDuration = (file: File): Promise<DurationData | null> => {
    return new Promise((resolve) => {
      // For .360 files, estimate duration based on file size
      if (file.name.toLowerCase().endsWith('.360')) {
        // 4GB = 8 minutes, so 1GB = 2 minutes
        const sizeInGB = file.size / (1024 * 1024 * 1024);
        const estimatedDurationMinutes = sizeInGB * 2;
        const estimatedDurationSeconds = estimatedDurationMinutes * 60;

        console.log(`Fichier .360 détecté: ${file.name}`);
        console.log(`Taille: ${sizeInGB.toFixed(2)} GB`);
        console.log(`Durée estimée: ${estimatedDurationMinutes.toFixed(1)} minutes`);

        resolve({
          duration: estimatedDurationSeconds,
          isEstimated: true
        });
        return;
      }

      // For other formats, use HTML5 Video API
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve({
          duration: video.duration,
          isEstimated: false
        });
      };

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(null);
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const checkVideoFile = async (file: File): Promise<VideoCheckResult> => {
    return new Promise((resolve) => {
      const result: VideoCheckResult = {
        file,
        status: 'checking'
      };

      // Extract video duration first
      extractVideoDuration(file).then((duration) => {
        new GoProMetaDataReader(file, (data: GoProMetadata | string) => {
          if (data === 'error') {
            result.status = 'error';
            result.error = 'Erreur lors de la lecture du fichier';
          } else if (data === 'undefined') {
            result.status = 'undefined';
            result.error = 'ID de caméra non trouvé (fichier possiblement corrompu)';
          } else if (typeof data === 'object') {
            result.status = 'valid';
            result.cameraId = data.cameraId;
            result.videoDate = data.videoDate;
          } else {
            // Fallback for backward compatibility
            result.status = 'valid';
            result.cameraId = data;
          }

          // Add duration to result
          result.duration = duration || undefined;

          resolve(result);
        });
      }).catch((error) => {
        result.status = 'error';
        result.error = `Erreur: ${error}`;
        resolve(result);
      });
    });
  };

  const startChecking = async () => {
    if (selectedFiles.length === 0) return;

    setIsChecking(true);
    setCheckResults([]);
    setCurrentCheckIndex(0);

    const results: VideoCheckResult[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      setCurrentCheckIndex(i);
      const result = await checkVideoFile(selectedFiles[i]);
      results.push(result);
      setCheckResults([...results]);
    }

    setIsChecking(false);
  };

  // Format duration for display
  const formatDuration = (durationData: DurationData | undefined): string => {
    if (!durationData) return 'N/A';

    const seconds = durationData.duration;
    if (!seconds || isNaN(seconds)) return 'N/A';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Generate timeline scale
  const generateTimelineScale = (firstDate: Date, lastDate: Date): React.JSX.Element => {
    const totalTimeSpan = lastDate.getTime() - firstDate.getTime();

    // If total duration is very short (less than 5 minutes), don't display scale
    if (totalTimeSpan < 5 * 60 * 1000) {
      return <div className="timeline-scale"></div>;
    }

    // Determine appropriate interval based on total duration
    let intervalMs: number;
    let labelFormat: Intl.DateTimeFormatOptions;

    if (totalTimeSpan <= 60 * 60 * 1000) { // Less than 1 hour
      intervalMs = 10 * 60 * 1000; // Every 10 minutes
      labelFormat = { hour: '2-digit', minute: '2-digit' };
    } else if (totalTimeSpan <= 6 * 60 * 60 * 1000) { // Less than 6 hours
      intervalMs = 30 * 60 * 1000; // Every 30 minutes
      labelFormat = { hour: '2-digit', minute: '2-digit' };
    } else if (totalTimeSpan <= 24 * 60 * 60 * 1000) { // Less than 1 day
      intervalMs = 60 * 60 * 1000; // Every hour
      labelFormat = { hour: '2-digit', minute: '2-digit' };
    } else { // More than 1 day
      intervalMs = 6 * 60 * 60 * 1000; // Every 6 hours
      labelFormat = { day: '2-digit', hour: '2-digit', minute: '2-digit' };
    }

    // Round first date to next interval
    const firstTime = firstDate.getTime();
    const startInterval = Math.ceil(firstTime / intervalMs) * intervalMs;

    const ticks: React.JSX.Element[] = [];

    // Generate ticks
    for (let time = startInterval; time < lastDate.getTime(); time += intervalMs) {
      const tickDate = new Date(time);
      const position = ((time - firstTime) / totalTimeSpan) * 100;

      // Determine if it's a major tick (every 2-3 graduations)
      const tickIndex = Math.round((time - startInterval) / intervalMs);
      const isMajor = tickIndex % 2 === 0;

      ticks.push(
        <div
          key={time}
          className={`timeline-tick ${isMajor ? 'major' : ''}`}
          style={{ left: `${position}%` }}
        />
      );

      // Add label only for major ticks
      if (isMajor) {
        ticks.push(
          <div
            key={`label-${time}`}
            className="timeline-tick-label"
            style={{ left: `${position}%` }}
          >
            {tickDate.toLocaleTimeString('fr-FR', labelFormat)}
          </div>
        );
      }
    }

    return <div className="timeline-scale">{ticks}</div>;
  };

  // Timeline component
  const renderTimeline = (): React.JSX.Element => {
    // Filter results with valid date and duration and sort them
    const validResults = checkResults.filter(r => r.videoDate && r.duration);
    if (validResults.length === 0) {
      return (
        <p className="text-muted text-center">
          Aucune vidéo avec date et durée valides trouvée.
        </p>
      );
    }

    // Sort by date (oldest first)
    const sortedResults = [...validResults].sort((a, b) => {
      return a.videoDate!.getTime() - b.videoDate!.getTime();
    });

    // Calculate durations in seconds and end dates
    const videoData: TimelineVideoData[] = sortedResults.map(r => {
      const duration = r.duration!.duration || 0;
      const startTime = r.videoDate!.getTime();
      const endTime = startTime + (duration * 1000); // Convert seconds to milliseconds
      return {
        result: r,
        duration: duration,
        startTime: startTime,
        endTime: endTime
      };
    });

    // Calculate total time span (from start of first video to end of last)
    const firstStartTime = Math.min(...videoData.map(v => v.startTime));
    const lastEndTime = Math.max(...videoData.map(v => v.endTime));
    const totalTimeSpan = lastEndTime - firstStartTime;

    const firstDate = new Date(firstStartTime);
    const lastDate = new Date(lastEndTime);

    return (
      <div className="timeline-container" ref={timelineContainerRef}>
        <div className="timeline-axis"></div>

        {videoData.map((video, index) => {
          const statusClass = video.result.status;

          if (totalTimeSpan > 0) {
            // Calculate start position (where video begins)
            const startPosition = ((video.startTime - firstStartTime) / totalTimeSpan) * 100;

            // Calculate width based on actual video duration (same logic as HTML version)
            const durationWidth = (video.duration * 1000 / totalTimeSpan) * 100; // Percentage of timeline

            // Convert to pixels for display (with minimum width) - adjusted for better proportions
            const containerWidth = timelineContainerRef.current?.parentElement?.clientWidth || 800;
            const calculatedWidth = (durationWidth / 100) * containerWidth;
            // Use a smaller minimum width and only apply it for very short videos
            const widthInPixels = calculatedWidth < 5 ? Math.max(5, calculatedWidth) : calculatedWidth;

            const durationText = formatDuration(video.result.duration);
            const dateText = video.result.videoDate!.toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            });

            return (
              <div
                key={index}
                className={`timeline-item ${statusClass}`}
                style={{ left: `${startPosition}%`, width: `${widthInPixels}px` }}
              >
                <div className="timeline-tooltip">
                  <strong>{video.result.file.name}</strong><br />
                  📅 {dateText}<br />
                  ⏱️ {durationText}{video.result.duration?.isEstimated ? ' (estimée)' : ''}<br />
                  📏 {(video.result.file.size / (1024 * 1024)).toFixed(1)} MB
                </div>
              </div>
            );
          } else {
            // Fallback if all videos have same date
            const position = sortedResults.length > 1 ?
              (index / (sortedResults.length - 1)) * 100 :
              50;

            const durationText = formatDuration(video.result.duration);
            const dateText = video.result.videoDate!.toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            });

            return (
              <div
                key={index}
                className={`timeline-item ${statusClass}`}
                style={{ left: `${position}%`, width: '100px' }}
              >
                <div className="timeline-tooltip">
                  <strong>{video.result.file.name}</strong><br />
                  📅 {dateText}<br />
                  ⏱️ {durationText}{video.result.duration?.isEstimated ? ' (estimée)' : ''}<br />
                  📏 {(video.result.file.size / (1024 * 1024)).toFixed(1)} MB
                </div>
              </div>
            );
          }
        })}

        {generateTimelineScale(firstDate, lastDate)}

        <div className="timeline-labels">
          <span>
            {firstDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}<br />
            <small>{firstDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
          </span>
          <span>
            {lastDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}<br />
            <small>{lastDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
          </span>
        </div>
      </div>
    );
  };

  const getStatusIcon = (status: VideoCheckResult['status']) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="text-success" />;
      case 'corrupted':
      case 'undefined':
        return <XCircle className="text-danger" />;
      case 'error':
        return <ExclamationTriangle className="text-warning" />;
      case 'checking':
        return <div className="spinner-border spinner-border-sm" role="status" />;
      default:
        return null;
    }
  };

  const getStatusText = (result: VideoCheckResult) => {
    switch (result.status) {
      case 'valid':
        return `Valide`;
      case 'undefined':
        return 'Corrompu';
      case 'error':
        return `Erreur: ${result.error}`;
      case 'checking':
        return 'Vérification...';
      default:
        return 'En attente';
    }
  };

  const getStatusVariant = (status: VideoCheckResult['status']) => {
    switch (status) {
      case 'valid':
        return 'success';
      case 'undefined':
      case 'corrupted':
        return 'danger';
      case 'error':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const validCount = checkResults.filter(r => r.status === 'valid').length;
  const corruptedCount = checkResults.filter(r => r.status === 'undefined' || r.status === 'corrupted').length;
  const errorCount = checkResults.filter(r => r.status === 'error').length;

  // Sort results by video date (oldest first)
  const sortedResults = [...checkResults].sort((a, b) => {
    // Prioritize videos with valid date
    if (a.videoDate && !b.videoDate) return -1;
    if (!a.videoDate && b.videoDate) return 1;

    // If both have date, sort by date (oldest first)
    if (a.videoDate && b.videoDate) {
      return a.videoDate.getTime() - b.videoDate.getTime();
    }

    // If no date, sort by filename
    return a.file.name.localeCompare(b.file.name);
  });

  return (
    <>
      <style>{`
        .upload-area {
          border: 2px dashed #007bff;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .upload-area:hover {
          background-color: #f8f9fa;
          border-color: #0056b3;
        }
        .upload-area.dragover {
          background-color: #e3f2fd;
          border-color: #1976d2;
        }
        .result-item {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 10px;
        }
        .status-valid { border-left: 4px solid #28a745; }
        .status-error { border-left: 4px solid #dc3545; }
        .status-undefined { border-left: 4px solid #ffc107; }
        .status-checking { border-left: 4px solid #17a2b8; }
        
        /* Timeline styles */
        .timeline-container {
          position: relative;
          padding: 10px 0;
        }
        .timeline-axis {
          position: relative;
          height: 2px;
          background-color: #dee2e6;
          margin: 10px 0;
        }
        .timeline-item {
          position: absolute;
          top: -10px;
          height: 22px;
          border-radius: 3px;
          border: 1px solid #000000;
          cursor: pointer;
          transition: all 0.5s ease, width 0.5s ease, left 0.5s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          color: white;
          text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
          min-width: 15px;
        }
        .timeline-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        .timeline-item.valid {
          background-color: #28a745;
        }
        .timeline-item.undefined {
          background-color: #dc3545;
        }
        .timeline-item.error {
          background-color: #ffc107;
          color: #212529;
        }
        .timeline-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
          font-size: 12px;
          color: #6c757d;
        }
        .timeline-scale {
          position: relative;
          height: 10px;
          margin-top: 5px;
        }
        .timeline-tick {
          position: absolute;
          width: 1px;
          height: 8px;
          background-color: #6c757d;
          top: 0;
        }
        .timeline-tick.major {
          height: 10px;
          background-color: #495057;
          width: 2px;
        }
        .timeline-tick-label {
          position: absolute;
          font-size: 10px;
          color: #6c757d;
          top: 12px;
          transform: translateX(-50%);
          white-space: nowrap;
        }
        .timeline-tooltip {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #212529;
          color: white;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          z-index: 1000;
        }
        .timeline-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #212529;
        }
        .timeline-item:hover .timeline-tooltip {
          opacity: 1;
        }
      `}</style>

      <Container className="py-4">
        <Row>
          <Col>
            <h1 className="mb-4">🎥 Vérification des vidéos GoPro</h1>

            <Alert variant="warning" className="mb-4">
              <div className="d-flex">
                <div className="me-3">
                  <ExclamationTriangle size={24} />
                </div>
                <div>
                  <h6 className="alert-heading mb-2">⚠️ Important - Date de Création des Vidéos</h6>
                  <p className="mb-2">
                    <strong>Pour obtenir la date exacte de prise de vue des vidéos GoPro, il est recommandé de vérifier les fichiers directement sur la carte SD de la caméra.</strong>
                  </p>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">📁 Sélection des Fichiers</h5>
              </Card.Header>
              <Card.Body>
                <div
                  ref={uploadAreaRef}
                  className="upload-area"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept="video/*,.360,.mp4,.mov,.avi"
                    style={{ display: 'none' }}
                  />
                  <div className="mb-2">
                    <Upload size={32} className="text-primary mb-2" />
                  </div>
                  <h6>Cliquez pour sélectionner des vidéos</h6>
                  <p className="text-muted mb-0">ou glissez-déposez vos fichiers ici</p>
                  <small className="text-muted">Formats supportés: MP4, MOV, AVI</small>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-3">
                    <p><strong>{selectedFiles.length}</strong> fichier(s) sélectionné(s)</p>
                    <Button
                      variant="success"
                      onClick={startChecking}
                      disabled={isChecking}
                      className="w-100"
                    >
                      {isChecking ? 'Vérification en cours...' : 'Commencer la Vérification'}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            {(checkResults.length > 0 || isChecking) && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">📊 Résumé</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="text-center">
                    <Col>
                      <div className="text-success">
                        <CheckCircle size={24} />
                        <div className="mt-1">
                          <strong>{validCount}</strong>
                          <br />
                          <small>Valides</small>
                        </div>
                      </div>
                    </Col>
                    <Col>
                      <div className="text-danger">
                        <XCircle size={24} />
                        <div className="mt-1">
                          <strong>{corruptedCount}</strong>
                          <br />
                          <small>Corrompues</small>
                        </div>
                      </div>
                    </Col>
                    <Col>
                      <div className="text-warning">
                        <ExclamationTriangle size={24} />
                        <div className="mt-1">
                          <strong>{errorCount}</strong>
                          <br />
                          <small>Erreurs</small>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {isChecking && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Progression</span>
                        <span>{currentCheckIndex + 1} / {selectedFiles.length}</span>
                      </div>
                      <ProgressBar
                        now={((currentCheckIndex + 1) / selectedFiles.length) * 100}
                        animated
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {checkResults.length > 0 && (
          <Row>
            <Col>
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">📅 Frise Chronologique</h5>
                  <small className="text-muted">Longueur des barres proportionnelle à la durée des vidéos</small>
                </Card.Header>
                <Card.Body>
                  {renderTimeline()}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {checkResults.length > 0 && (
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📋 Résultats Détaillés</h5>
                    <small className="text-muted">
                      <ArrowUp className="me-1" />
                      Triées par date de prise (plus ancien en premier)
                    </small>
                  </div>
                </Card.Header>
                <Card.Body>
                  {sortedResults.map((result, index) => (
                    <div key={index} className={`result-item status-${result.status}`}>
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          {getStatusIcon(result.status)}
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-bold">{result.file.name}</div>
                          <div className="text-muted small">
                            Taille: {(result.file.size / (1024 * 1024)).toFixed(2)} MB
                            {result.duration && (
                              <>
                                <br />⏱️ Durée: {formatDuration(result.duration)}{result.duration.isEstimated ? ' (estimée)' : ''}
                              </>
                            )}
                            {result.videoDate && (
                              <>
                                <br />Date de prise: {result.videoDate.toLocaleString('fr-FR', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-end">
                          <Badge bg={getStatusVariant(result.status)}>
                            {getStatusText(result)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default VideoCorruptionCheckPage;
